import type { VehicleSpec } from './types';

/**
 * Vehicle roster. All makes and models are original and fictional; the names lean on period
 * British motoring (coachbuilders, county towns, heraldry) without referencing real marques.
 *
 * Tuning philosophy (details in docs/DRIVING.md): arcade grip well above 1 g, rear axle slightly
 * grippier than the front for stability, handbrake that drops rear grip hard so slides are a
 * deliberate choice. Each class has one exaggerated trait:
 *   compact  — darts around corners, tiny turning circle, folds up in a crash
 *   saloon   — planted, heavy, rams things out of the way
 *   van      — slow, tall, wide arcs, rolls into corners
 *   coupe    — fast and eager, power oversteer, rewards a light touch
 *   police   — a saloon with better brakes, more grip and more engine
 *   cab      — famously tight turning circle, sluggish
 *   lorry    — a slow battering ram
 *   estate   — the practical one: a saloon's manners with a long load deck, steady and forgiving
 *   exec     — long, heavy and fast: the villains' car, flies in a straight line, wide arcs, a battering ram
 *   roadster — tiny and nimble: turns on a sixpence and darts through gaps, but folds up in a crash
 */
const S = (s: VehicleSpec): VehicleSpec => s;

export const VEHICLE_SPECS: Record<string, VehicleSpec> = {
  compact: S({
    id: 'compact',
    displayName: 'Austral Minnow',
    class: 'compact',
    body: 'hatch',
    mass: 780,
    width: 1.56,
    length: 3.3,
    height: 1.38,
    wheelbase: 2.1,
    frontWeight: 0.6,
    drive: 'front',
    accel: 7.2,
    maxSpeed: 36,
    maxReverse: 9,
    brake: 15,
    engineBrake: 1.2,
    frontGrip: 1.4,
    rearGrip: 1.5,
    tyreStiffness: 0.85,
    handbrakeGrip: 0.3,
    powerOversteer: 0.15,
    steerAngle: 0.62,
    steerFalloffSpeed: 19,
    minSteer: 0.24,
    steerSpeed: 7.5,
    counterSteer: 0.3,
    yawDamping: 2.2,
    weightTransfer: 0.35,
    drag: 0.0005,
    rolling: 0.35,
    inertiaScale: 1.15,
    maxHealth: 70,
    fragility: 1.35,
    restitution: 0.25,
    gears: 4,
    idleRpm: 900,
    redlineRpm: 6800,
    paints: [0x9b2d20, 0x2f4f3a, 0xd8c690, 0x2b3b5c, 0xe4e0d4, 0x6b4a2f, 0xc77f2a],
    trafficWeight: 4,
  }),
  saloon: S({
    id: 'saloon',
    displayName: 'Halberd Regent',
    class: 'saloon',
    body: 'saloon',
    mass: 1450,
    width: 1.84,
    length: 4.75,
    height: 1.42,
    wheelbase: 2.85,
    frontWeight: 0.55,
    drive: 'rear',
    accel: 6.0,
    maxSpeed: 45,
    maxReverse: 9,
    brake: 13,
    engineBrake: 1.3,
    frontGrip: 1.28,
    rearGrip: 1.38,
    tyreStiffness: 0.75,
    handbrakeGrip: 0.24,
    powerOversteer: 0.35,
    steerAngle: 0.56,
    steerFalloffSpeed: 23,
    minSteer: 0.19,
    steerSpeed: 5.5,
    counterSteer: 0.4,
    yawDamping: 2.6,
    weightTransfer: 0.3,
    drag: 0.0003,
    rolling: 0.32,
    inertiaScale: 1.45,
    maxHealth: 115,
    fragility: 0.8,
    restitution: 0.15,
    gears: 4,
    idleRpm: 750,
    redlineRpm: 5200,
    paints: [0x1b1d22, 0x5a1c1e, 0x334a5e, 0x8c8c86, 0x3c3226, 0xbfb59a, 0x2d4033],
    trafficWeight: 4,
  }),
  van: S({
    id: 'van',
    displayName: 'Corran Haulmaster',
    class: 'van',
    body: 'van',
    mass: 2100,
    width: 2.0,
    length: 5.0,
    height: 2.35,
    wheelbase: 3.15,
    frontWeight: 0.56,
    drive: 'rear',
    accel: 4.8,
    maxSpeed: 33,
    maxReverse: 7,
    brake: 10,
    engineBrake: 1.6,
    frontGrip: 1.05,
    rearGrip: 1.15,
    tyreStiffness: 0.6,
    handbrakeGrip: 0.35,
    powerOversteer: 0.25,
    steerAngle: 0.48,
    steerFalloffSpeed: 17,
    minSteer: 0.2,
    steerSpeed: 4.5,
    counterSteer: 0.35,
    yawDamping: 3,
    weightTransfer: 0.45,
    drag: 0.0006,
    rolling: 0.4,
    inertiaScale: 1.7,
    maxHealth: 140,
    fragility: 0.7,
    restitution: 0.1,
    gears: 4,
    idleRpm: 700,
    redlineRpm: 4500,
    paints: [0xe8e4d6, 0x1f3a5f, 0x7b2d1d, 0x4b5a3a, 0xc9a227],
    trafficWeight: 2,
  }),
  coupe: S({
    id: 'coupe',
    displayName: 'Vesper GT',
    class: 'sports',
    body: 'coupe',
    mass: 1150,
    width: 1.8,
    length: 4.3,
    height: 1.28,
    wheelbase: 2.5,
    frontWeight: 0.48,
    drive: 'rear',
    accel: 10.5,
    maxSpeed: 58,
    maxReverse: 10,
    brake: 17,
    engineBrake: 1.1,
    frontGrip: 1.68,
    rearGrip: 1.72,
    tyreStiffness: 0.8,
    handbrakeGrip: 0.18,
    powerOversteer: 0.85,
    steerAngle: 0.55,
    steerFalloffSpeed: 28,
    minSteer: 0.16,
    steerSpeed: 8.5,
    counterSteer: 0.38,
    yawDamping: 1.8,
    weightTransfer: 0.25,
    drag: 0.0002,
    rolling: 0.3,
    inertiaScale: 1.25,
    maxHealth: 85,
    fragility: 1.1,
    restitution: 0.2,
    gears: 5,
    idleRpm: 1000,
    redlineRpm: 7800,
    paints: [0xb81d13, 0xf2c14e, 0x0f5257, 0xeeeeee, 0x111111, 0x3d6b3a],
    trafficWeight: 1,
  }),
  police: S({
    id: 'police',
    displayName: 'Halberd Regent Patrol',
    class: 'police',
    body: 'saloon',
    mass: 1500,
    width: 1.84,
    length: 4.75,
    height: 1.5,
    wheelbase: 2.85,
    frontWeight: 0.55,
    drive: 'rear',
    accel: 8.0,
    maxSpeed: 50,
    maxReverse: 10,
    brake: 15,
    engineBrake: 1.3,
    frontGrip: 1.4,
    rearGrip: 1.5,
    tyreStiffness: 0.8,
    handbrakeGrip: 0.24,
    powerOversteer: 0.3,
    steerAngle: 0.56,
    steerFalloffSpeed: 25,
    minSteer: 0.19,
    steerSpeed: 6.5,
    counterSteer: 0.45,
    yawDamping: 2.6,
    weightTransfer: 0.3,
    drag: 0.00025,
    rolling: 0.32,
    inertiaScale: 1.5,
    maxHealth: 140,
    fragility: 0.6,
    restitution: 0.15,
    gears: 4,
    idleRpm: 800,
    redlineRpm: 5800,
    paints: [0xeeeeea],
    trafficWeight: 0,
  }),
  policevan: S({
    id: 'policevan',
    displayName: 'Corran Haulmaster Patrol Van',
    class: 'police',
    body: 'van',
    mass: 2200,
    width: 2.0,
    length: 5.0,
    height: 2.35,
    wheelbase: 3.15,
    frontWeight: 0.56,
    drive: 'rear',
    accel: 6.2,
    maxSpeed: 40,
    maxReverse: 8,
    brake: 12,
    engineBrake: 1.6,
    frontGrip: 1.2,
    rearGrip: 1.3,
    tyreStiffness: 0.65,
    handbrakeGrip: 0.35,
    powerOversteer: 0.25,
    steerAngle: 0.48,
    steerFalloffSpeed: 18,
    minSteer: 0.2,
    steerSpeed: 4.8,
    counterSteer: 0.35,
    yawDamping: 3,
    weightTransfer: 0.45,
    drag: 0.0006,
    rolling: 0.4,
    inertiaScale: 1.7,
    maxHealth: 190,
    fragility: 0.6,
    restitution: 0.1,
    gears: 4,
    idleRpm: 700,
    redlineRpm: 4500,
    paints: [0x1c2a4a],
    trafficWeight: 0,
  }),
  cab: S({
    id: 'cab',
    displayName: 'Halberd Hackney',
    class: 'service',
    body: 'cab',
    mass: 1600,
    width: 1.8,
    length: 4.55,
    height: 1.78,
    wheelbase: 2.8,
    frontWeight: 0.56,
    drive: 'rear',
    accel: 5.2,
    maxSpeed: 38,
    maxReverse: 8,
    brake: 12,
    engineBrake: 1.5,
    frontGrip: 1.2,
    rearGrip: 1.32,
    tyreStiffness: 0.7,
    handbrakeGrip: 0.28,
    powerOversteer: 0.25,
    steerAngle: 0.7,
    steerFalloffSpeed: 18,
    minSteer: 0.18,
    steerSpeed: 6,
    counterSteer: 0.4,
    yawDamping: 2.8,
    weightTransfer: 0.4,
    drag: 0.0005,
    rolling: 0.38,
    inertiaScale: 1.5,
    maxHealth: 120,
    fragility: 0.8,
    restitution: 0.15,
    gears: 4,
    idleRpm: 700,
    redlineRpm: 4400,
    paints: [0x141414, 0x141414, 0x3b2a4a],
    trafficWeight: 1.5,
  }),
  lorry: S({
    id: 'lorry',
    displayName: 'Bedwell Tipper',
    class: 'heavy',
    body: 'lorry',
    mass: 5200,
    width: 2.4,
    length: 7.2,
    height: 3.1,
    wheelbase: 4.4,
    frontWeight: 0.5,
    drive: 'rear',
    accel: 3.4,
    maxSpeed: 25,
    maxReverse: 6,
    brake: 7.5,
    engineBrake: 1.8,
    frontGrip: 0.95,
    rearGrip: 1.08,
    tyreStiffness: 0.55,
    handbrakeGrip: 0.45,
    powerOversteer: 0.1,
    steerAngle: 0.52,
    steerFalloffSpeed: 13,
    minSteer: 0.22,
    steerSpeed: 3.5,
    counterSteer: 0.3,
    yawDamping: 3.5,
    weightTransfer: 0.4,
    drag: 0.0009,
    rolling: 0.45,
    inertiaScale: 1.8,
    maxHealth: 260,
    fragility: 0.4,
    restitution: 0.08,
    gears: 5,
    idleRpm: 600,
    redlineRpm: 3200,
    paints: [0x8a3b22, 0x2e4a6b, 0xb08a2e, 0x43513a],
    trafficWeight: 0.6,
  }),
  estate: S({
    id: 'estate',
    displayName: 'Corran Drover Estate',
    class: 'saloon',
    body: 'estate',
    mass: 1400,
    width: 1.78,
    length: 4.6,
    height: 1.48,
    wheelbase: 2.75,
    frontWeight: 0.53,
    drive: 'rear',
    accel: 5.6,
    maxSpeed: 42,
    maxReverse: 9,
    brake: 13,
    engineBrake: 1.3,
    frontGrip: 1.25,
    rearGrip: 1.36,
    tyreStiffness: 0.72,
    handbrakeGrip: 0.26,
    powerOversteer: 0.3,
    steerAngle: 0.58,
    steerFalloffSpeed: 22,
    minSteer: 0.19,
    steerSpeed: 5.8,
    counterSteer: 0.42,
    yawDamping: 2.7,
    weightTransfer: 0.32,
    drag: 0.00035,
    rolling: 0.33,
    inertiaScale: 1.5,
    maxHealth: 125,
    fragility: 0.8,
    restitution: 0.15,
    gears: 4,
    idleRpm: 750,
    redlineRpm: 5000,
    paints: [0x6e5a3c, 0x2d4033, 0xbfb59a, 0x334a5e, 0x8c8c86, 0x7a3b22, 0xd8cfb4],
    trafficWeight: 2.5,
  }),
  exec: S({
    id: 'exec',
    displayName: 'Ashcombe Grandee',
    class: 'saloon',
    body: 'exec',
    mass: 1750,
    width: 1.9,
    length: 5.2,
    height: 1.45,
    wheelbase: 3.1,
    frontWeight: 0.54,
    drive: 'rear',
    accel: 7.4,
    maxSpeed: 53,
    maxReverse: 9,
    brake: 14,
    engineBrake: 1.2,
    frontGrip: 1.34,
    rearGrip: 1.44,
    tyreStiffness: 0.76,
    handbrakeGrip: 0.22,
    powerOversteer: 0.4,
    steerAngle: 0.52,
    steerFalloffSpeed: 25,
    minSteer: 0.17,
    steerSpeed: 5.2,
    counterSteer: 0.42,
    yawDamping: 2.8,
    weightTransfer: 0.28,
    drag: 0.00022,
    rolling: 0.3,
    inertiaScale: 1.6,
    maxHealth: 150,
    fragility: 0.65,
    restitution: 0.12,
    gears: 4,
    idleRpm: 650,
    redlineRpm: 5000,
    paints: [0x111214, 0x3a1418, 0x1c2a3e, 0x9a9a94, 0xb8a988, 0x23302a],
    trafficWeight: 0.8,
  }),
  roadster: S({
    id: 'roadster',
    displayName: 'Vesper Linnet',
    class: 'sports',
    body: 'roadster',
    mass: 820,
    width: 1.5,
    length: 3.6,
    height: 1.22,
    wheelbase: 2.1,
    frontWeight: 0.5,
    drive: 'rear',
    accel: 8.6,
    maxSpeed: 50,
    maxReverse: 9,
    brake: 16,
    engineBrake: 1.2,
    frontGrip: 1.6,
    rearGrip: 1.66,
    tyreStiffness: 0.86,
    handbrakeGrip: 0.2,
    powerOversteer: 0.55,
    steerAngle: 0.64,
    steerFalloffSpeed: 24,
    minSteer: 0.2,
    steerSpeed: 9,
    counterSteer: 0.34,
    yawDamping: 2,
    weightTransfer: 0.28,
    drag: 0.00035,
    rolling: 0.3,
    inertiaScale: 1.1,
    maxHealth: 65,
    fragility: 1.4,
    restitution: 0.25,
    gears: 4,
    idleRpm: 950,
    redlineRpm: 7200,
    paints: [0xb81d13, 0x2f5a3a, 0xe4e0d4, 0x2b3b5c, 0xd8c690, 0x111111],
    trafficWeight: 1,
  }),
};

/** Specs the player can sensibly drive in missions / the sandbox. */
export const PLAYER_VEHICLE_IDS = ['compact', 'saloon', 'van', 'coupe', 'police', 'cab', 'lorry', 'estate', 'exec', 'roadster'] as const;

export function getSpec(id: string): VehicleSpec {
  const s = VEHICLE_SPECS[id];
  if (!s) throw new Error(`Unknown vehicle spec '${id}'. Known: ${Object.keys(VEHICLE_SPECS).join(', ')}`);
  return s;
}
